import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Loader2, Mail, Phone } from "lucide-react";
import { MaskedLines, FadeUp, Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Monthly Bookkeeping",
  "Catch-Up / Cleanup Bookkeeping",
  "QuickBooks Online Setup or Support",
  "Tax Preparation",
  "Estimated Tax Planning",
  "Pennsylvania Sales Tax",
  "Payroll Support",
  "Other",
];

const EMPTY = {
  name: "",
  business_name: "",
  email: "",
  phone: "",
  industry: "",
  services: [],
  software: "",
  books_status: "",
  message: "",
  preferred_contact: "email",
};

const inputCls =
  "w-full rounded-md border border-hairline bg-cream/60 px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 placeholder:text-stone2/50 focus:border-pine focus:ring-2 focus:ring-pine/15";

const labelCls = "block text-xs font-semibold uppercase tracking-wide text-stone2 mb-2";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleService = (s) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email so I can reply.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Thank you! Your inquiry is on its way.");
      window.scrollTo(0, 0);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <section className="ledger-lines relative overflow-hidden pb-16 pt-36 lg:pt-44" data-testid="contact-header">
        <div className="pointer-events-none absolute inset-y-0 left-8 hidden w-px bg-gold/40 lg:block" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-6">
          <FadeUp>
            <Overline>Get Started</Overline>
          </FadeUp>
          <MaskedLines
            as="h1"
            delay={0.1}
            lines={["Let's talk about", "your business."]}
            className="mt-6 font-serif text-5xl font-medium leading-[1.05] tracking-tight text-ink sm:text-6xl lg:text-7xl"
          />
          <FadeUp delay={0.35}>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-stone2">
              Have a question or interested in working with Arnold Bookkeeping? Reach out
              directly or complete the short form below.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="contact-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12">
          {/* Direct contact */}
          <div className="space-y-6 lg:col-span-5">
            <FadeUp>
              <a
                href="mailto:Arnold.Bookkeeping@outlook.com"
                data-testid="contact-email-card"
                className="group flex items-center gap-5 rounded-2xl border border-hairline bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-parchment text-pine transition-colors duration-300 group-hover:bg-pine group-hover:text-gold">
                  <Mail size={20} />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs font-semibold uppercase tracking-wide text-stone2">Email</span>
                  <span className="mt-1 block break-all font-semibold text-ink">Arnold.Bookkeeping@outlook.com</span>
                </span>
              </a>
            </FadeUp>
            <FadeUp delay={0.1}>
              <a
                href="tel:7175541224"
                data-testid="contact-phone-card"
                className="group flex items-center gap-5 rounded-2xl border border-hairline bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-parchment text-pine transition-colors duration-300 group-hover:bg-pine group-hover:text-gold">
                  <Phone size={20} />
                </span>
                <span>
                  <span className="block text-xs font-semibold uppercase tracking-wide text-stone2">Phone</span>
                  <span className="mt-1 block font-semibold text-ink">717.554.1224</span>
                </span>
              </a>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="rounded-2xl bg-pine-950 p-8 text-cream" data-testid="contact-personal-note">
                <Overline light>Tell me about your business</Overline>
                <p className="mt-5 font-serif text-2xl italic leading-snug text-cream/90">
                  Please provide a little information so I can better understand what you may
                  need.
                </p>
                <p className="mt-5 text-sm leading-relaxed text-cream/60">
                  You'll work directly with Aaron Arnold — no call centers, no account
                  managers. Proudly serving small businesses in Wellsville and across Central
                  Pennsylvania.
                </p>
              </div>
            </FadeUp>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <FadeUp delay={0.15}>
              {done ? (
                <div
                  className="flex h-full flex-col items-center justify-center rounded-2xl border border-hairline bg-white p-12 text-center"
                  data-testid="contact-success-panel"
                >
                  <CheckCircle2 size={56} className="text-pine" />
                  <h2 className="mt-6 font-serif text-3xl font-semibold text-ink">
                    Thank you, {form.name.split(" ")[0]}.
                  </h2>
                  <p className="mt-3 max-w-md leading-relaxed text-stone2">
                    Your inquiry has been sent. I'll be in touch shortly — usually within one
                    business day.
                  </p>
                  <button
                    data-testid="contact-send-another-button"
                    onClick={() => {
                      setForm(EMPTY);
                      setDone(false);
                    }}
                    className="mt-8 inline-flex items-center gap-2 rounded-md border border-pine/25 px-6 py-3 text-sm font-semibold text-pine transition-colors duration-200 hover:border-pine"
                  >
                    Send another inquiry
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid="contact-form"
                  className="rounded-2xl border border-hairline bg-white p-8 shadow-sm lg:p-10"
                >
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label className={labelCls} htmlFor="cf-name">Name *</label>
                      <input
                        id="cf-name"
                        data-testid="contact-name-input"
                        className={inputCls}
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="cf-business">Business Name</label>
                      <input
                        id="cf-business"
                        data-testid="contact-business-input"
                        className={inputCls}
                        value={form.business_name}
                        onChange={(e) => set("business_name", e.target.value)}
                        placeholder="Your business"
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="cf-email">Email *</label>
                      <input
                        id="cf-email"
                        type="email"
                        data-testid="contact-email-input"
                        className={inputCls}
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="cf-phone">Phone Number</label>
                      <input
                        id="cf-phone"
                        type="tel"
                        data-testid="contact-phone-input"
                        className={inputCls}
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        placeholder="717-555-0123"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className={labelCls} htmlFor="cf-industry">Type of Business / Industry</label>
                    <input
                      id="cf-industry"
                      data-testid="contact-industry-input"
                      className={inputCls}
                      value={form.industry}
                      onChange={(e) => set("industry", e.target.value)}
                      placeholder="e.g. Landscaping, salon, rental properties"
                    />
                  </div>

                  <fieldset className="mt-8">
                    <legend className={labelCls}>What services are you interested in?</legend>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {SERVICES.map((s) => (
                        <label
                          key={s}
                          data-testid={`contact-service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                          className={`flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                            form.services.includes(s)
                              ? "border-pine bg-parchment text-pine"
                              : "border-hairline bg-cream/60 text-stone2 hover:border-pine/40"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-[#143E2B]"
                            checked={form.services.includes(s)}
                            onChange={() => toggleService(s)}
                          />
                          {s}
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <div className="mt-8">
                    <label className={labelCls} htmlFor="cf-software">
                      What accounting software are you currently using?
                    </label>
                    <input
                      id="cf-software"
                      data-testid="contact-software-input"
                      className={inputCls}
                      value={form.software}
                      onChange={(e) => set("software", e.target.value)}
                      placeholder="e.g. QuickBooks Online, spreadsheets, nothing yet"
                    />
                  </div>

                  <fieldset className="mt-8">
                    <legend className={labelCls}>Are your books currently up to date?</legend>
                    <div className="flex flex-wrap gap-3">
                      {["Yes", "Mostly", "No", "Not Sure"].map((opt) => (
                        <label
                          key={opt}
                          data-testid={`contact-books-option-${opt.toLowerCase().replace(/\s/g, "-")}`}
                          className={`cursor-pointer rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-200 ${
                            form.books_status === opt
                              ? "border-pine bg-pine text-cream"
                              : "border-hairline bg-cream/60 text-stone2 hover:border-pine/40"
                          }`}
                        >
                          <input
                            type="radio"
                            name="books_status"
                            className="sr-only"
                            checked={form.books_status === opt}
                            onChange={() => set("books_status", opt)}
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <div className="mt-8">
                    <label className={labelCls} htmlFor="cf-message">
                      Briefly describe what you would like help with.
                    </label>
                    <textarea
                      id="cf-message"
                      data-testid="contact-message-textarea"
                      rows={4}
                      className={`${inputCls} resize-y`}
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      placeholder="A few sentences about your business and what you need…"
                    />
                  </div>

                  <fieldset className="mt-8">
                    <legend className={labelCls}>Preferred method of contact</legend>
                    <div className="flex gap-3">
                      {["Phone", "Email"].map((opt) => (
                        <label
                          key={opt}
                          data-testid={`contact-preference-option-${opt.toLowerCase()}`}
                          className={`cursor-pointer rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-200 ${
                            form.preferred_contact === opt.toLowerCase()
                              ? "border-pine bg-pine text-cream"
                              : "border-hairline bg-cream/60 text-stone2 hover:border-pine/40"
                          }`}
                        >
                          <input
                            type="radio"
                            name="preferred_contact"
                            className="sr-only"
                            checked={form.preferred_contact === opt.toLowerCase()}
                            onChange={() => set("preferred_contact", opt.toLowerCase())}
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit-button"
                    className="mt-10 inline-flex w-full items-center justify-center gap-2 rounded-md bg-pine px-8 py-4 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-gold hover:text-ink disabled:opacity-60 sm:w-auto"
                  >
                    {sending ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Send my inquiry <ArrowUpRight size={16} />
                      </>
                    )}
                  </button>
                  <p className="mt-5 font-mono text-[11px] leading-relaxed text-stone2/70">
                    Prefer email? Copy your answers to Arnold.Bookkeeping@outlook.com and I'll
                    take it from there.
                  </p>
                </form>
              )}
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
